import { css } from '@emotion/react'
import gql from 'graphql-tag'
import React, { useCallback } from 'react'
import toastr from 'toastr'
import { useMutation } from '../../../../api/graphql'
import Modal from '../../misc/Modal'
import { OrganizationMembershipsQuery } from './OrganizationsSection'

const BULK_ASSIGN_MUTATION = gql`
  mutation EventsBulkAssignOrganization($input: EventsBulkAssignOrganizationInput!) {
    eventsBulkAssignOrganization(input: $input) {
      updatedCount
      errors {
        attribute
        message
      }
    }
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
  organizationId: string
  organizationName: string
  personalEventsCount: number
}

const MigrateEventsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  organizationId,
  organizationName,
  personalEventsCount,
}) => {
  const [bulkAssign, { loading }] = useMutation(BULK_ASSIGN_MUTATION, {
    refetchQueries: [{ query: OrganizationMembershipsQuery }],
  })

  const handleMigrate = useCallback(async () => {
    try {
      const result = await bulkAssign({ variables: { input: { organizationId } } })
      const updatedCount = result.data?.eventsBulkAssignOrganization?.updatedCount
      if (updatedCount != null) {
        toastr.success(`${updatedCount} event${updatedCount === 1 ? '' : 's'} moved to ${organizationName}!`)
      }
      onClose()
    } catch (error) {
      toastr.error('Something went wrong. Please try again.')
      console.error(error)
    }
  }, [bulkAssign, organizationId, organizationName, onClose])

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose}>
      <h2
        css={css`
          margin: 0 0 8px 0;
          font-size: 20px;
          color: #102638;
        `}
      >
        Move events to {organizationName}?
      </h2>
      <p
        css={css`
          margin: 0 0 24px 0;
          color: #6b7280;
          font-size: 14px;
          line-height: 1.6;
        `}
      >
        You have <strong>{personalEventsCount}</strong> event{personalEventsCount === 1 ? '' : 's'} not linked to an
        organization. Would you like to move {personalEventsCount === 1 ? 'it' : 'them'} under{' '}
        <strong>{organizationName}</strong>?
        <br />
        <br />
        This makes {personalEventsCount === 1 ? 'it' : 'them'} visible to all organization members and keeps everything
        in one place.
      </p>
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        `}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          css={css`
            background: transparent;
            color: #6b7280;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            padding: 12px 20px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.15s ease-out;
            &:hover:not(:disabled) {
              background: #f9fafb;
              border-color: #9ca3af;
            }
          `}
        >
          No thanks
        </button>
        <button
          type="button"
          onClick={handleMigrate}
          disabled={loading}
          css={css`
            background: #a855f7;
            color: white;
            border: none;
            border-radius: 6px;
            padding: 12px 24px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: background-color 0.15s ease-out;
            &:hover:not(:disabled) {
              background: #9333ea;
            }
            &:disabled {
              opacity: 0.7;
              cursor: not-allowed;
            }
          `}
        >
          {loading ? 'Moving...' : `Yes, move ${personalEventsCount === 1 ? 'it' : 'them'}`}
        </button>
      </div>
    </Modal>
  )
}

export default MigrateEventsModal
